import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserProfile } from "@shared/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NAV_ITEMS } from "@shared/constants";
import { useTheme } from "@/lib/utils";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";
import { LocationSelector } from "@/components/common/LocationSelector";
import { OrganizationSwitcher } from "@/components/common/OrganizationSwitcher";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useQuery } from "@tanstack/react-query";
import { AIControl } from "@/components/Dashboard/AIControl";

interface HeaderProps {
  user: UserProfile;
  onSidebarToggle: () => void;
  onLogout: () => void;
}

export function Header({ user, onSidebarToggle, onLogout }: HeaderProps) {
  const [location, setLocation] = useLocation();
  const { theme, setTheme } = useTheme();
  const { selectedLocationId, setSelectedLocationId, locations } = useLocationContext();
  const { currentOrganizationId, currentOrganization, setOrganizationId } = useOrganization();

  // Get the current page name from location
  const getCurrentPageName = () => {
    const navItems = Object.values(NAV_ITEMS);
    const exactMatch = navItems.find((item) => item.path === location);
    if (exactMatch) return exactMatch.name;

    const prefixMatch = navItems
      .filter((item) => item.path !== "/" && location.startsWith(`${item.path}/`))
      .sort((a, b) => b.path.length - a.path.length)[0];
    if (prefixMatch) return prefixMatch.name;

    const firstSegment = location.split("?")[0].split("/").filter(Boolean)[0];
    if (!firstSegment) return "Dashboard";
    return firstSegment
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  };

  // Toggle dark mode
  const toggleDarkMode = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  // Check if user is super admin
  const isSuperAdmin = user.isSuperAdmin === true;

  // Mentions inbox (org scoped)
  const inboxQuery = useQuery({
    queryKey: ['mentions-inbox', currentOrganizationId],
    queryFn: async () => {
      const url = `/api/mentions/inbox${currentOrganizationId ? `?organizationId=${currentOrganizationId}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch inbox');
      return res.json();
    },
    staleTime: 0,
  });
  const unreadCount = Array.isArray(inboxQuery.data) ? inboxQuery.data.filter((n: any) => !n.seen_at).length : 0;
  const profileHref = `/profile/${user.id}${currentOrganizationId ? `?organizationId=${currentOrganizationId}` : ""}`;

  return (
    <header className={`${isSuperAdmin
      ? 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800'
      : 'bg-white border-gray-200 dark:bg-neutral-800 dark:border-neutral-700'} shadow-sm border-b min-h-16 flex flex-wrap md:flex-nowrap items-center justify-between gap-y-2 px-2 py-2 sm:px-4 sticky top-0 z-20`}>
      <div className="flex min-w-0 max-w-full flex-wrap md:flex-nowrap items-center gap-y-2">
        {/* Sidebar menu on mobile and desktop */}
        <button
          aria-label="Toggle sidebar"
          className={`flex h-10 w-10 shrink-0 items-center justify-center mr-2 ${isSuperAdmin
            ? 'text-gray-600 hover:text-gray-800 dark:text-neutral-300 dark:hover:text-neutral-100'
            : 'text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200'}`}
          onClick={(event) => { event.stopPropagation(); onSidebarToggle(); }}
        >
          <span className="mdi mdi-menu text-2xl"></span>
        </button>

        {/* Breadcrumb */}
        <div className="flex min-w-0 items-center md:max-w-[180px]">
          <a
            href="/"
            className={isSuperAdmin
              ? 'text-gray-600 hover:text-gray-800 dark:text-neutral-300 dark:hover:text-neutral-100'
              : 'text-gray-500 hover:text-primary dark:text-neutral-400 dark:hover:text-primary-light'}
          >
            Dashboard
          </a>
          {getCurrentPageName() !== 'Dashboard' && <>
          <span className="mdi mdi-chevron-right text-gray-400 mx-2 text-sm"></span>
          <span className="min-w-0 truncate text-gray-800 dark:text-white" title={getCurrentPageName()}>
            {getCurrentPageName()}
          </span>
          </>}
        </div>

        {/* Organization Switcher - Only show for super admin or users with multi-org access */}
        {(isSuperAdmin || (user.organizationIds && user.organizationIds.length > 1)) && (
          <>
            <div className="h-6 w-px shrink-0 bg-gray-200 dark:bg-neutral-700 mx-2" />
            <OrganizationSwitcher
              currentOrganizationId={currentOrganizationId || user.organizationId}
              onOrganizationChange={setOrganizationId}
              className="min-w-0 max-w-[190px]"
            />
          </>
        )}

        {/* Location Selector with Multi-Select */}
        <div className="ml-2 max-w-full">
          <LocationSelector 
            showAllOption={true}
            multiSelect={true}
            showSelectAll={true}
          />
        </div>
      </div>

      <div className="flex shrink-0 flex-nowrap items-center gap-1 lg:gap-2 ml-auto">
        {/* AI Control Panel - Only show for super admin */}
        {isSuperAdmin && (
          <div className="hidden lg:flex items-center overflow-visible relative">
            <AIControl />
          </div>
        )}

        {/* Search */}
        <div className="relative hidden xl:block">
          <Input
            type="text"
            placeholder="Search..."
            className="w-64 pl-10 pr-4 py-2"
          />
          <span className="mdi mdi-magnify text-gray-400 absolute left-3 top-2.5"></span>
        </div>

        {/* Dark mode toggle */}
        <Button
          variant="ghost"
          size="icon"
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={toggleDarkMode}
          className="rounded-full"
        >
          <span className="mdi mdi-theme-light-dark text-gray-600 dark:text-neutral-300 text-xl"></span>
        </Button>

        {/* AI Assistant */}
        <span title="Bolt AI is coming soon">
          <Button variant="ghost" size="icon" className="rounded-full" disabled aria-label="Bolt AI is coming soon">
            <span className="mdi mdi-robot text-muted-foreground text-xl" aria-hidden="true"></span>
          </Button>
        </span>

        {/* Notifications (Inbox) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full relative" aria-label="Notifications" title="Notifications">
              <span className="mdi mdi-bell-outline text-gray-600 dark:text-neutral-300 text-xl"></span>
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-4 px-1 bg-red-600 text-white text-[10px] rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 p-0">
            <div className="p-3 border-b text-sm font-semibold">Notifications</div>
            <div className="max-h-72 overflow-y-auto">
              {inboxQuery.isLoading && (
                <div className="p-3 text-sm text-neutral-500">Loading…</div>
              )}
              {inboxQuery.isError && (
                <div className="p-3 text-sm text-red-600">Failed to load notifications</div>
              )}
              {Array.isArray(inboxQuery.data) && inboxQuery.data.length === 0 && (
                <div className="p-3 text-sm text-neutral-500">No notifications</div>
              )}
              {Array.isArray(inboxQuery.data) && inboxQuery.data.map((n: any) => (
                <div key={n.id} className="px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="font-medium capitalize">{n.type}</span>
                      <span className="text-neutral-500"> • {n.entity}</span>
                    </div>
                    {!n.seen_at && <span className="w-2 h-2 bg-blue-500 rounded-full" />}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Account menu"
              title="Account menu"
              className="rounded-full bg-primary text-white"
            >
              <span className="mdi mdi-account text-lg"></span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              <div>
                <div className="font-medium">{user.fullName}</div>
                <div className="text-sm text-muted-foreground">{user.email}</div>
                {currentOrganizationId && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Organization: {currentOrganization?.id === currentOrganizationId ? currentOrganization.name : 'Not selected'}
                  </div>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => setLocation(profileHref)}
              onSelect={(event) => {
                event.preventDefault();
                setLocation(profileHref);
              }}
            >
              <span className="mdi mdi-account-circle-outline mr-2"></span>
              Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setLocation("/settings")}
              onSelect={(event) => {
                event.preventDefault();
                setLocation("/settings");
              }}
            >
              <span className="mdi mdi-cog-outline mr-2"></span>
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onLogout()}
              onSelect={(event) => {
                event.preventDefault();
                onLogout();
              }}
            >
              <span className="mdi mdi-logout-variant mr-2"></span>
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
